/**
 * All categories page (sections/all-categories.liquid).
 *
 *  - View switch: A–Z categories ⇄ by venue & zone. Both views are in the HTML; this
 *    only toggles `hidden` on the two views and their two jump rails. No query param,
 *    no history entry — the two views are one page, and as separate URLs they would be
 *    two crawlable clones of it (docs/plans/gsc-coverage-2026-07.md).
 *  - Filter: matches the typed text against a category name and hides everything else.
 *    A group in the venue view matches on its own name OR on any of its leaves, so
 *    typing "kegerator" keeps "Beer dispensing" on screen with the leaf still inside it.
 *    Empty containers (letter section, zone, venue) fold away so the page never shows
 *    a heading with nothing under it.
 *
 * Without this file the page still works: the A–Z view is the one rendered visible,
 * the venue view sits open below it, and every link is a plain link.
 */
(function () {
  var root = document.querySelector('[data-all-categories]');
  if (!root) return;

  var views = {};
  var rails = {};
  root.querySelectorAll('[data-view]').forEach(function (el) { views[el.dataset.view] = el; });
  root.querySelectorAll('[data-rail]').forEach(function (el) { rails[el.dataset.rail] = el; });

  var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-mode]'));
  var hint = root.querySelector('[data-mode-hint]');
  var input = root.querySelector('[data-filter]');
  var empty = root.querySelector('[data-empty]');

  var cells = Array.prototype.slice.call(root.querySelectorAll('.all-cats__cell'));
  var letters = Array.prototype.slice.call(root.querySelectorAll('.all-cats__letter'));
  var nodes = Array.prototype.slice.call(root.querySelectorAll('.all-cats__node'));
  var zones = Array.prototype.slice.call(root.querySelectorAll('.all-cats__zone'));
  var venues = Array.prototype.slice.call(root.querySelectorAll('.all-cats__venue'));

  var mode = 'az';

  // What an anchor has to scroll clear of: the sticky filter strip under the fixed
  // header. Its height is not one number — it stacks into two rows below 992px, and the
  // venue rail wraps to a second line where the letter rail does not — so it is measured
  // rather than guessed, and written back as the --all-cats-strip the CSS offsets read.
  // Without it a jump lands with the section's own heading behind the strip.
  var util = root.querySelector('.all-cats__util');
  function measureStrip() {
    if (!util) return;
    root.style.setProperty('--all-cats-strip', util.offsetHeight + 'px');
  }
  window.addEventListener('resize', measureStrip);

  // Sticky tells you nothing about whether it has actually stuck, and the strip's top
  // corners have to straighten when it docks against the header. A sentinel one pixel
  // above it answers that without a scroll handler: the moment it leaves the top of the
  // viewport — minus the fixed header, which is what the strip stops under — the strip
  // is docked.
  if (util && 'IntersectionObserver' in window) {
    var sentinel = document.createElement('div');
    sentinel.className = 'all-cats__util-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    util.parentNode.insertBefore(sentinel, util);
    var watch = function () {
      var offset = Math.round(parseFloat(getComputedStyle(util).top) || 0);
      return new IntersectionObserver(function (entries) {
        util.classList.toggle('is-stuck', !entries[0].isIntersecting);
      }, { rootMargin: '-' + offset + 'px 0px 0px 0px', threshold: 0 });
    };
    var observer = watch();
    observer.observe(sentinel);
    // --header-height changes at 992px, so the margin the observer was built with stops
    // being the offset the strip parks at.
    window.addEventListener('resize', function () {
      observer.disconnect();
      observer = watch();
      observer.observe(sentinel);
    });
  }

  // A rail button whose section did not render — every collection under that letter or
  // venue turned out empty — would scroll nowhere. The rails are built before the views,
  // so the only honest place to catch it is here, against the DOM that actually exists.
  root.querySelectorAll('.all-cats__jump').forEach(function (link) {
    var id = link.getAttribute('href');
    if (id && id.charAt(0) === '#' && !root.querySelector(id)) link.remove();
  });

  // A card matches on its name and on the teaser printed under it — typing "keg" or
  // "gluten" should keep the category that says so on screen. Read off the DOM rather
  // than duplicated into an attribute, so the filter can only ever match text the
  // visitor can actually see (the teaser is truncated in Liquid, and this is the
  // truncated copy).
  cells.forEach(function (cell) {
    var teaser = cell.querySelector('.cat-card__teaser');
    cell.dataset.search = ((cell.dataset.name || '') + ' ' + (teaser ? teaser.textContent : '')).toLowerCase();
  });

  // A group matches on its own name or on any leaf under it — collected once, since
  // the DOM does not change while the page lives.
  nodes.forEach(function (node) {
    var names = [node.dataset.name || ''];
    node.querySelectorAll('.all-cats__leaves [data-name]').forEach(function (leaf) {
      names.push(leaf.dataset.name || '');
    });
    node.dataset.search = names.join(' ');
  });

  function show(el, on) {
    if (on) el.removeAttribute('hidden');
    else el.setAttribute('hidden', '');
  }

  function setMode(next) {
    mode = next;
    buttons.forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.mode === next)); });
    Object.keys(views).forEach(function (k) { show(views[k], k === next); });
    Object.keys(rails).forEach(function (k) { show(rails[k], k === next); });
    if (hint) hint.textContent = hint.dataset[next === 'az' ? 'hintAz' : 'hintVenue'] || '';
    measureStrip();
    apply();
  }

  function apply() {
    var q = (input ? input.value : '').trim().toLowerCase();
    var hits = 0;

    if (mode === 'az') {
      cells.forEach(function (cell) {
        var on = !q || (cell.dataset.search || '').indexOf(q) > -1;
        show(cell, on);
        if (on) hits++;
      });
      letters.forEach(function (section) {
        show(section, !!section.querySelector('.all-cats__cell:not([hidden])'));
      });
    } else {
      nodes.forEach(function (node) {
        var on = !q || (node.dataset.search || '').indexOf(q) > -1;
        show(node, on);
        if (on) hits++;
      });
      zones.forEach(function (zone) {
        show(zone, !!zone.querySelector('.all-cats__node:not([hidden])'));
      });
      venues.forEach(function (venue) {
        var on = !!venue.querySelector('.all-cats__zone:not([hidden])');
        show(venue, on);
        // A filtered-down venue opens itself: a collapsed one would read as "no matches
        // here", which is the opposite of what the filter just found.
        if (q && on) venue.open = true;
      });
    }

    if (empty) show(empty, hits === 0);
  }

  if (input) input.addEventListener('input', apply);

  // Which view to open on. The switch does not mint a URL — as query params these two views
  // would be crawlable clones of one page — but it does have to survive leaving the page and
  // coming back: someone who was reading the venue view, opened a category, then a product,
  // and pressed Back twice does not expect to land in the alphabet.
  //
  // So the choice rides in the URL FRAGMENT, which the browser restores with the history
  // entry and which crawlers do not treat as a separate URL, and is mirrored into
  // sessionStorage for the return trips that arrive without one. A letter or venue anchor in
  // the fragment answers the question by itself — you cannot be jumping to a venue from the
  // alphabet.
  var VENUE_HASH = '#by-venue';
  function storedMode() {
    try { return sessionStorage.getItem('all-cats-view'); } catch (e) { return null; }
  }
  function rememberMode(m) {
    try { sessionStorage.setItem('all-cats-view', m); } catch (e) { /* private mode */ }
  }
  function modeFromUrl() {
    var h = location.hash;
    if (h === VENUE_HASH || h.indexOf('#all-cats-venue-') === 0) return 'venue';
    if (h.indexOf('#all-cats-') === 0) return 'az';
    return null;
  }

  buttons.forEach(function (b) {
    b.addEventListener('click', function () {
      setMode(b.dataset.mode);
      rememberMode(b.dataset.mode);
      // replaceState, not push: Back belongs to the page you came from, not to the view you
      // were looking at a second ago.
      history.replaceState(null, '',
        b.dataset.mode === 'venue' ? VENUE_HASH : location.pathname + location.search);
    });
  });

  setMode(modeFromUrl() || storedMode() || 'az');
  window.addEventListener('hashchange', function () {
    var m = modeFromUrl();
    if (m && m !== mode) setMode(m);
  });
})();
